// The interview invitation a recruiter sends BY HAND.
//
// ─── WHY THIS IS A MODULE AND NOT A FUNCTION INSIDE THE PIPELINE ────────────
//
// Three things have to say the same words to a candidate:
//
//   1. the emailed invitation  (backend, utils/hiringMailer.sendInterviewInvite)
//   2. the WhatsApp button     (CandidatePipeline)
//   3. "Copy"                  (CandidatePipeline)
//
// (2) and (3) were separate strings, and drifted: one warned the interview was
// recorded and the other did not. A candidate reached on WhatsApp and one
// reached by paste got different briefs for the same job, and nothing on any
// screen said which they had been sent.
//
// One definition, in a file with no React in it, so it can be read and tested
// on its own — the words a stranger reads about a job they applied for are
// worth being able to check without rendering a table to do it.
//
// (1) still lives in the backend, because the email is composed where it is
// sent. The two are kept deliberately parallel: same order, same four
// warnings, same sign-off rule. If you change one, change the other.
//
// ─── WHAT MUST NOT BE HARDCODED HERE ────────────────────────────────────────
//
// The duration and the sign-off come in as arguments, from the rubric and from
// the job. They used to be literals — "about 30 minutes" and a generic
// closing — and a literal here is a promise that silently stops being true the
// first time somebody edits the job. The candidate budgets their evening
// against that number.

/**
 * @param {object}  row         the pipeline row — needs candidate_name and interview_url
 * @param {object}  context
 * @param {string}  context.jobTitle
 * @param {number}  [context.durationMinutes]  from the active rubric
 * @param {object}  [context.jd]               { url, name } — linked, not attached
 * @param {string}  [context.signature]        the job's own sign-off
 * @returns {string} plain text, ready to paste into WhatsApp, SMS or email
 */
export function buildInviteMessage(row, context = {}) {
  const {
    jobTitle = "the role",
    durationMinutes,
    jd = null,
    signature = null,
  } = context;

  // "there" rather than an empty gap. A message opening "Hi ," reads as broken
  // software, which is not the first impression a hiring process wants to make.
  const first = String(row?.candidate_name || "").trim().split(/\s+/)[0] || "there";

  // Falls back to 30 only when the rubric genuinely carries no figure.
  const minutes = Number(durationMinutes) > 0 ? Number(durationMinutes) : 30;

  const lines = [
    `Hi ${first},`,
    "",
    `Thanks for applying for ${jobTitle}. We would like you to take the ` +
      `first-round interview, which you can do online whenever suits you.`,
    "",
    "Before you start, please note:",
    `• It takes about ${minutes} minutes.`,
    "• Your answers are video recorded and reviewed by our team.",
    "• You need a working camera and microphone, and a quiet spot.",
    "• Each question is timed, so finish in one sitting if you can.",
    "",
    "Start your interview here:",
    row?.interview_url || "",
  ];

  // The JD as a LINK, because a clipboard cannot carry a file. It is the same
  // document the emailed invitation attaches — the blob is public-read, which
  // is what makes both delivery routes possible from one upload.
  if (jd?.url) {
    lines.push("", `Full job description: ${jd.url}`);
  }

  lines.push("", "The link is personal to you, so please do not forward it.");

  // The job's own sign-off when it has one, so a pasted invitation is signed by
  // the same person the emailed one is. Whitespace-only counts as absent —
  // otherwise a stray space produces a message that just stops.
  const signed =
    typeof signature === "string" && signature.trim() ? signature.trim() : null;
  lines.push("", signed || `— ${jobTitle} Hiring Team`);

  return lines.join("\n");
}

export default buildInviteMessage;
